import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row, Table } from 'react-bootstrap';
import * as SelfWarehousesApi from '../../api/SelfWarehouses';
import { SelfWarehouse } from '../../api/SelfWarehouses';
import { SelfMetaGroupsDrawer } from './SelfMetaGroupsDrawer';

type FormState = {
  external_id: string;
  name: string;
  address: string;
  is_default: boolean;
  reserve_priority: string;
  is_active: boolean;
};

const emptyForm = (): FormState => ({
  external_id: '',
  name: '',
  address: '',
  is_default: false,
  reserve_priority: '',
  is_active: true,
});

const Warehouses = () => {
  const [rows, setRows] = useState<SelfWarehouse[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [metaSelf, setMetaSelf] = useState<SelfWarehouse | null>(null);

  const load = useCallback(async () => {
    setPending(true);
    try {
      setRows(await SelfWarehousesApi.getAll());
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    setError('');
    setMessage('');
  };

  const openEdit = (w: SelfWarehouse) => {
    setEditingId(w.id);
    setForm({
      external_id: w.external_id,
      name: w.name,
      address: w.address || '',
      is_default: w.is_default,
      reserve_priority: String(w.reserve_priority),
      is_active: w.is_active !== false,
    });
    setShowForm(true);
    setError('');
    setMessage('');
  };

  const handleSubmit = async () => {
    setError('');
    setMessage('');
    try {
      const priorityRaw = form.reserve_priority.trim();
      const reserve_priority =
        priorityRaw === '' ? undefined : Number(priorityRaw);
      if (
        reserve_priority != null &&
        (!Number.isFinite(reserve_priority) || !Number.isInteger(reserve_priority))
      ) {
        setError('Приоритет списания — целое число (меньше = раньше)');
        return;
      }
      if (editingId == null) {
        await SelfWarehousesApi.create({
          external_id: form.external_id,
          name: form.name || undefined,
          address: form.address || null,
          is_default: form.is_default,
          reserve_priority,
        });
        setMessage('Склад создан');
      } else {
        await SelfWarehousesApi.update(editingId, {
          external_id: form.external_id,
          name: form.name,
          address: form.address || null,
          reserve_priority,
          is_active: form.is_active,
        });
        setMessage('Склад обновлён');
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка сохранения');
    }
  };

  const handleSetDefault = async (w: SelfWarehouse) => {
    setError('');
    setMessage('');
    try {
      await SelfWarehousesApi.setDefault(w.id);
      setMessage(`«${w.name}» — склад по умолчанию`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка');
    }
  };

  const handleDelete = async (w: SelfWarehouse) => {
    if (!window.confirm(`Удалить склад «${w.name}»?`)) return;
    setError('');
    setMessage('');
    try {
      await SelfWarehousesApi.remove(w.id);
      setMessage('Склад удалён');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка удаления');
    }
  };

  return (
    <Container>
      <Row>
        <Col>
          <Button style={{ margin: '16px 0' }} onClick={openCreate}>
            Создать склад
          </Button>
        </Col>
      </Row>

      {showForm && (
        <Row style={{ marginBottom: 16, padding: 16, border: '1px solid #ddd' }}>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label>external_id (1С / синтетический)</Form.Label>
              <Form.Control
                value={form.external_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, external_id: e.target.value }))
                }
                placeholder="например warehouse-1"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Название</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Склад по умолчанию"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Адрес</Form.Label>
              <Form.Control
                value={form.address}
                onChange={(e) =>
                  setForm((f) => ({ ...f, address: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Приоритет списания</Form.Label>
              <Form.Control
                type="number"
                value={form.reserve_priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reserve_priority: e.target.value }))
                }
                placeholder="1 = первый в водопаде"
              />
              <Form.Text muted>
                Меньше число — раньше списывается резерв объединённых складов МП.
                Пусто при создании — в конец списка. Лимиты заказов — кнопка
                «Лимиты / группы» в строке склада.
              </Form.Text>
            </Form.Group>
            {editingId != null && (
              <Form.Check
                className="mb-2"
                type="checkbox"
                label="Активен (учитывать в остатках МП)"
                checked={form.is_active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_active: e.target.checked }))
                }
              />
            )}
            {editingId == null && (
              <Form.Check
                className="mb-2"
                type="checkbox"
                label="Сделать складом по умолчанию"
                checked={form.is_default}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_default: e.target.checked }))
                }
              />
            )}
            <Button onClick={handleSubmit} style={{ marginRight: 8 }}>
              Сохранить
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setError('');
              }}
            >
              Отмена
            </Button>
          </Col>
        </Row>
      )}

      {error && <div style={{ color: 'crimson', marginBottom: 8 }}>{error}</div>}
      {message && <div style={{ color: 'green', marginBottom: 8 }}>{message}</div>}

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>ID</th>
            <th>external_id</th>
            <th>Название</th>
            <th>Адрес</th>
            <th>Приоритет списания</th>
            <th>Активен</th>
            <th>По умолчанию</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.id}>
              <td>{w.id}</td>
              <td>{w.external_id}</td>
              <td>{w.name}</td>
              <td>{w.address || '—'}</td>
              <td>{w.reserve_priority}</td>
              <td>{w.is_active === false ? 'нет' : 'да'}</td>
              <td>{w.is_default ? 'да' : ''}</td>
              <td>
                <Button
                  size="sm"
                  variant="outline-primary"
                  style={{ marginRight: 4 }}
                  onClick={() => openEdit(w)}
                >
                  Изменить
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ marginRight: 4 }}
                  onClick={() => setMetaSelf(w)}
                >
                  Лимиты / группы
                </Button>
                {!w.is_default && (
                  <Button
                    size="sm"
                    variant="outline-success"
                    style={{ marginRight: 4 }}
                    onClick={() => handleSetDefault(w)}
                  >
                    Сделать default
                  </Button>
                )}
                {!w.is_default && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(w)}
                  >
                    Удалить
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {pending && <div>Загрузка…</div>}
      {!pending && !rows.length && <div>Нет складов</div>}

      <SelfMetaGroupsDrawer
        open={!!metaSelf}
        selfWarehouse={metaSelf}
        onClose={() => setMetaSelf(null)}
        onSelfChanged={(updated) => {
          setMetaSelf((prev) =>
            prev?.id === updated.id
              ? { ...prev, order_limit_self: updated.order_limit_self }
              : prev,
          );
          setRows((prev) =>
            prev.map((r) =>
              r.id === updated.id
                ? { ...r, order_limit_self: updated.order_limit_self }
                : r,
            ),
          );
        }}
      />
    </Container>
  );
};

export default Warehouses;
